import { cloneDeep, difference } from 'lodash';
import { Message } from '@meitu/saas-ui';
import { getBackgroundLayerProps, isFitHeight } from '@/utils/layer-kit';
import {
    DirectionType,
    LayerType,
    MAX_STAGES,
    MAX_STAGES_TIP,
    RenderMode,
    StretchOptionType,
    type IExtraInfo,
    type ILayerProps,
    type IPosition,
    type ISize,
    type IStageResizeData,
} from '@/version-types';
import type { IReplaceLayerResult, IService } from '../../shared';
import { trackConfig } from '../history/track-func';
import type { ServiceManager } from '../service-manager';
import {
    historyAdjustType,
    historyBaseType,
    historyReplaceLayerType,
    StageHistory,
    type IAdjustUnit,
    type IStageHistoryBaseData,
} from './stage-history';

export class StageService implements IService {
    constructor(readonly service: ServiceManager) {}

    onInit() {}

    onBindEvent() {
        new StageHistory(this.service);
    }

    private canInsertStages(extraInfos: IExtraInfo[]) {
        const { edgelessCanvas, edgelessPPT, frame } = this.service;
        if (edgelessCanvas.isEdgelessMode) {
            const pptFrameCount = extraInfos.filter((extraInfo) => extraInfo?.isPPT).length;
            return pptFrameCount === 0 || edgelessPPT.canAddPPTFrameInfos(extraInfos);
        }

        return frame.getFrameIdsLength() + extraInfos.length <= MAX_STAGES;
    }

    private warnMaxStages() {
        Message.warning(MAX_STAGES_TIP());
    }

    /** 插入舞台 */
    private async insertStage(
        layers: ILayerProps[],
        extraInfo: IExtraInfo,
        scaleWidth: boolean,
        index?: number,
        trackFunc?: string,
    ) {
        if (!this.canInsertStages([extraInfo])) {
            this.warnMaxStages();
            return [];
        }

        this.service.frame.delActiveLayer();
        const { originIndexs, insertId } = await this.service.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: scaleWidth ? [] : [layers],
              })
            : undefined;
        this.service.frame.addExtraInfos([extraInfo], originIndexs);
        const stageType = this.service.edgelessCanvas.renderMode;
        let stageScaleWidth = 0;
        if (stageType === RenderMode.VARIETY) {
            stageScaleWidth = this.service.frame.getSize().width;
        }
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.service.frame.addStages(layers, {
            activeNewStage: true,
            stageIndex: index,
            scaleWidth: stageScaleWidth || scaleWidth,
            originIndexes: originIndexs,
        });
        if (scaleWidth && insertData) {
            insertData.stages = [this.service.frame.getLayers(originIndexs[0])];
        }
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.service.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackFunc || trackConfig.page.replace,
            skipCheckOriginIndex: true,
        });

        return difference(newIds, oldIds);
    }

    /**
     * 创建空背景图层
     *
     * @param size - 尺寸
     * @param backgroundColor - 背景颜色
     * @param url - 背景图片
     * @returns 背景图层
     */
    private createEmptyBgLayer(size: ISize, backgroundColor: string = '#ffffff', url: string = '') {
        return getBackgroundLayerProps({
            width: size.width,
            height: size.height,
            data: {
                backgroundColor,
                url,
                naturalWidth: 0,
                naturalHeight: 0,
                backgroundGradient: null,
                imageTransform: {
                    offsetX: 0,
                    offsetY: 0,
                    rotate: 0,
                    scale: 1,
                    flipH: false,
                    flipV: false,
                    widthRatio: 1,
                    heightRatio: 1,
                },
                stretchOption: StretchOptionType.cover,
            },
        });
    }

    /** 获取空白舞台 */
    private getEmptyStage(removeExtraInfo = true) {
        const originIndex = this.service.frame.getActiveFrameId();
        const backgroundLayer = cloneDeep(this.service.frame.getBackgroundLayer(originIndex)) || ({} as ILayerProps);
        if (removeExtraInfo && backgroundLayer.extraInfo) {
            delete backgroundLayer.extraInfo.label;
        }
        const layers: ILayerProps[] = [{ ...backgroundLayer, ...this.createEmptyBgLayer(backgroundLayer) }];

        return layers;
    }

    /**
     * 替换激活舞台
     *
     * @param layers - 图层数据
     * @param extraInfo - 额外信息
     * @param scaleWidth - 是否缩放宽度
     * @param trackFunc - 跟踪函数
     */
    async replaceActive(layers: ILayerProps[], extraInfo: IExtraInfo, scaleWidth: boolean, trackFunc?: string) {
        this.service.frame.delActiveLayer();
        const { originIndexs, insertId } = await this.service.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: scaleWidth ? [] : [layers],
              })
            : undefined;
        this.service.frame.addExtraInfos([extraInfo], originIndexs);
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.service.frame.replaceActiveFrame(
            layers,
            scaleWidth,
            originIndexs,
        );
        if (scaleWidth && insertData) {
            insertData.stages = [this.service.frame.getLayers(originIndexs[0])];
        }
        this.service.history.write({
            type: historyBaseType,
            data: {
                insertId,
                oldIndex,
                oldIds,
                newIndex,
                newIds,
                _isReplaceStage: true,
                insertData,
                isReplaceActive: true,
            },
            trackFunc,
            skipCheckOriginIndex: true,
        });
    }

    /**
     * 替换所有舞台
     *
     * @param stages - 图层数据
     * @param extraInfos - 额外信息
     * @param trackFunc - 跟踪函数
     * @param stageTypes - 舞台类型
     */
    async replaceAll(
        stages: ILayerProps[][],
        extraInfos: IExtraInfo[],
        trackFunc?: string,
        stageTypes?: { oldStageType: RenderMode; newStageType: RenderMode },
    ) {
        this.service.frame.delActiveLayer();
        const { originIndexs, insertId } = await this.service.history.realEdit.insertStage(extraInfos.length);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: extraInfos,
                  stages: stages,
              })
            : undefined;
        this.service.frame.addExtraInfos(extraInfos, originIndexs);
        if (stageTypes) {
            // this.service.editor.setCanvasType(stageTypes.newStageType as any);
            this.service.edgelessCanvas.setRenderMode(stageTypes.newStageType);
        }
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.service.frame.replaceAllFrames(
            stages,
            originIndexs,
        );
        this.service.history.write({
            type: historyBaseType,
            data: {
                insertId,
                oldIndex,
                oldIds,
                newIndex,
                newIds,
                stageTypes,
                _isReplaceStage: true,
                insertData,
            },
            trackFunc,
            skipCheckOriginIndex: true,
        });
    }

    /**
     * 删除舞台
     *
     * @param stageIndex - 舞台索引序号
     */
    delete(stageIndex: number) {
        // this.service.frame.getActiveFrameId();
        const originIndex = this.service.frame.indexToFrameId(stageIndex);
        const { oldIds, oldIndex, newIds, newIndex } = this.service.frame.delStage(stageIndex);
        this.service.history.write({
            type: historyBaseType,
            data: {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
                delData: [originIndex],
            },
            trackFunc: trackConfig.page.delete,
            originIndex,
            skipCheckOriginIndex: true,
        });
    }

    /**
     * 批量删除舞台
     *
     * @param indices - 舞台索引序号列表
     */
    batchDelete(indices: number[]) {
        const res = this.service.frame.batchDelStage(indices);
        // console.log('batchDelete res', { indices, delData, res });
        if (!res) return;
        const { oldIds, oldIndex, newIds, newIndex } = res;
        const delData = oldIds.filter((frameId) => !newIds.includes(frameId));
        this.service.history.write({
            type: historyBaseType,
            data: {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
                delData,
            },
            trackFunc: trackConfig.page.delete,
            skipCheckOriginIndex: true,
        });

        return res;
    }

    /**
     * 添加空白画布，默认添加在最后
     *
     * @param stageIndex - 舞台索引序号
     * @param incomeExtraInfo - 额外信息
     * @param inheritBgInfo - 继承的背景信息
     * @param masterPage - 主页数据
     */
    async create(
        stageIndex = -1,
        incomeExtraInfo = { threshold: 0 },
        inheritBgInfo?: ILayerProps,
        masterPage?: ILayerProps[],
    ) {
        const activeExtraInfo = this.service.frame.getExtraInfo(this.service.frame.getActiveIndex());
        const extraInfo = {
            ...cloneDeep(activeExtraInfo),
            ...incomeExtraInfo,
        };
        if (!this.canInsertStages([extraInfo])) {
            this.warnMaxStages();
            return;
        }

        const emptyStage = this.getEmptyStage();
        if (masterPage) {
            masterPage.find((item) => {
                if (item.layerType === LayerType.TEXT_LAYER && item.extraInfo?.label?.type === '内容标题') {
                    item.extraInfo.modified = false;
                }
            });
            emptyStage.push(...masterPage);
            emptyStage.splice(0, 1);
            if (!extraInfo.extraInfo) {
                extraInfo.extraInfo = {};
            }
            extraInfo.extraInfo.label = {
                theme: '浅色调',
                type: '内容',
            };
        } else if (inheritBgInfo) {
            if (extraInfo.extraInfo?.label) {
                delete extraInfo.extraInfo.label;
            }
            emptyStage[0] = inheritBgInfo;
        } else {
            if (extraInfo.extraInfo?.label) {
                delete extraInfo.extraInfo.label;
            }
        }
        const { originIndexs, insertId } = await this.service.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: [emptyStage],
              })
            : undefined;
        this.service.frame.addExtraInfos([extraInfo], originIndexs);
        const { oldIds, oldIndex, newIds, newIndex } = this.service.frame.addStages(emptyStage, {
            activeNewStage: true,
            stageIndex: stageIndex === -1 ? undefined : stageIndex,
            scaleWidth: false,
            originIndexes: [],
        });
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.service.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackConfig.page.add,
            skipCheckOriginIndex: true,
        });
    }

    /**
     * 克隆舞台
     *
     * @param stageIndex - 舞台索引序号
     * @param targetIndex - 目标索引序号
     */
    async clone(stageIndex: number, targetIndex?: number) {
        targetIndex = targetIndex ?? stageIndex;
        const layers = this.service.frame.cloneFrame(stageIndex);
        const extraInfo = cloneDeep(this.service.frame.getExtraInfo(stageIndex));
        if (!this.canInsertStages([extraInfo])) {
            this.warnMaxStages();
            return [];
        }

        const { originIndexs, insertId } = await this.service.history.realEdit.insertStage(1);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: [extraInfo],
                  stages: [layers],
              })
            : undefined;
        this.service.frame.addExtraInfos([extraInfo], originIndexs);
        const { addList, oldIndex, oldIds, newIndex, newIds } = this.service.frame.addStages(layers, {
            activeNewStage: true,
            stageIndex: targetIndex,
            scaleWidth: false,
            originIndexes: originIndexs,
        });
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        this.service.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackConfig.page.copy,
            skipCheckOriginIndex: true,
        });

        return difference(newIds, oldIds);
    }

    /**
     * 调整尺寸
     *
     * @param stageIndex - 舞台索引序号
     * @param currentStages - 当前数据
     * @param destStages - 目标数据
     * @param currentSize - 当前大小
     * @param destSize - 目标大小
     * @param units - 当前、目标单位
     * @param pushHistory - 可选；是否添加到历史记录中；默认值为 `true`
     * @param trackFunc - 可选；跟踪函数
     */
    resize(
        stageIndex: number,
        currentStages: ILayerProps[][],
        destStages: ILayerProps[][],
        currentSize: ISize,
        destSize: ISize,
        units: IAdjustUnit,
        pushHistory = true,
        trackFunc?: string,
    ) {
        this.service.frame.delActiveLayer();
        const originIndex = this.service.frame.indexToFrameId(stageIndex);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            this.service.frame.resizeLayers(
                layers,
                currentSize,
                destSize,
                originIndex === this.service.frame.indexToFrameId(i),
            );
        }
        const newStages = cloneDeep(destStages);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            this.service.frame.batchUpdateLayer(layers, true);
        }

        // this.service.frame.rerender(originIndex);

        if (pushHistory) {
            this.service.history.write({
                type: historyAdjustType,
                data: {
                    originIndex,
                    units,
                    oldStages: currentStages,
                    newStages,
                },
                trackFunc: trackFunc || trackConfig[LayerType.BACKGROUND_LAYER]?.resize,
            });
        }
    }

    /**
     * 缩放尺寸
     *
     * @param stageIndex - 舞台索引序号
     * @param currentStages - 当前数据
     * @param destStages - 目标数据
     * @param currentSize - 当前大小
     * @param destSize - 目标大小
     * @param units - 当前、目标单位
     * @param pushHistory - 可选；是否添加到历史记录中；默认值为 `true`
     * @param trackFunc - 可选；跟踪函数
     */
    scale(
        stageIndex: number,
        currentStages: ILayerProps[][],
        destStages: ILayerProps[][],
        currentSize: ISize,
        destSize: ISize,
        units: IAdjustUnit,
        pushHistory = true,
        trackFunc?: string,
    ) {
        this.service.frame.delActiveLayer();
        const originIndex = this.service.frame.indexToFrameId(stageIndex);
        const useHeightScale = isFitHeight(currentSize, destSize);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            this.service.frame.scaleLayers(
                layers,
                currentSize,
                destSize,
                useHeightScale,
                originIndex === this.service.frame.indexToFrameId(i),
                true,
            );
        }
        const newStages = cloneDeep(destStages);
        for (let i = 0; i < destStages.length; i++) {
            const layers = destStages[i];
            this.service.frame.batchUpdateLayer(layers, false);
        }

        // this.service.frame.rerender(originIndex);

        if (pushHistory) {
            this.service.history.write({
                type: historyAdjustType,
                data: {
                    originIndex,
                    units,
                    oldStages: currentStages,
                    newStages,
                },
                trackFunc: trackFunc || trackConfig[LayerType.BACKGROUND_LAYER]?.resize,
            });
        }
    }

    /**
     * 替换图层
     *
     * @param ids - 旧图层 id 列表
     * @param newLayers - 新图层数据
     * @param historyFlag - 可选；历史记录标志
     * @param trackFunc - 可选；跟踪函数
     * @param autoActive - 可选；是否自动激活图层；默认值为 `true`
     * @param extraUpdateLayer - 可选；额外更新图层，不写进历史记录
     */
    replaceLayer(
        ids: string | string[],
        newLayers: ILayerProps | ILayerProps[],
        historyFlag?: unknown,
        trackFunc?: string,
        autoActive = true,
        extraUpdateLayer?: {
            oldLayers: ILayerProps[];
            newLayers: ILayerProps[];
        },
    ) {
        this.service.frame.delActiveLayer();
        const res = this.service.frame.replaceLayer(ids, newLayers);
        if (!res) return [];
        const { newIds, oldInfo: restoreData, activeId, wrapId } = res;
        this.service.history.write({
            type: historyReplaceLayerType,
            data: {
                activeId,
                wrapId,
                newIds,
                restoreData,
                ids,
                newLayers,
                historyFlag,
                extraUpdateLayer,
            },
            trackFunc,
        });
        if (autoActive) {
            [activeId, wrapId].forEach((id) => {
                if (id && this.service.flatLayer.getLayer(id)) {
                    this.service.frame.setActiveLayer(id);
                }
            });
        }
        return res.newIds;
    }

    /**
     * 静默替换图层，不记录历史，但确认时，需要通过 applyReplaceLayer 方法应用
     *
     * @param ids - 旧图层 id 列表
     * @param newLayers - 新图层数据
     * @returns 替换结果
     */
    muteReplaceLayer(ids: string | string[], newLayers: ILayerProps | ILayerProps[]) {
        this.service.frame.delActiveLayer();
        const res = this.service.frame.replaceLayer(ids, newLayers);
        if (!res) return null;
        const { newIds, activeId, wrapId } = res;

        [activeId, wrapId].forEach((id) => {
            if (id && this.service.flatLayer.getLayer(id)) {
                this.service.frame.setActiveLayer(id);
            }
        });

        return res;
    }

    /**
     * 静默恢复替换前的图层及其原始顺序，不记录历史
     *
     * 用于主图编辑取消时回滚临时合并结果，并保留替换前的激活状态。
     *
     * @param res - 静默替换图层时返回的替换结果
     * @returns 是否成功恢复
     */
    muteRestoreReplaceLayer(res: IReplaceLayerResult) {
        this.service.frame.delActiveLayer();
        this.service.frame.replaceLayer(res.newIds, undefined, res.oldInfo);
        const isRestored = res.oldInfo.every(({ layer }) => this.service.flatLayer.getLayer(layer.id));
        if (!isRestored) return false;

        [res.activeId, res.wrapId].forEach((id) => {
            if (id && this.service.flatLayer.getLayer(id)) {
                this.service.frame.setActiveLayer(id);
            }
        });

        return true;
    }

    /**
     * 应用替换图层
     *
     * @param ids - 旧图层 id 列表
     * @param res - 替换结果
     * @param newLayers - 新图层数据
     * @param historyFlag - 可选；历史记录标志
     * @param trackFunc - 可选；跟踪函数
     */
    applyReplaceLayer(
        ids: string | string[],
        res: IReplaceLayerResult,
        newLayers: ILayerProps | ILayerProps[],
        historyFlag?: unknown,
        trackFunc?: string,
    ) {
        const { newIds, oldInfo: restoreData, activeId, wrapId } = res;

        this.service.history.write({
            type: historyReplaceLayerType,
            data: {
                activeId,
                wrapId,
                newIds,
                restoreData,
                ids,
                newLayers,
                historyFlag,
            },
            trackFunc,
        });

        return res.newIds;
    }

    /**
     * 交换舞台
     *
     * @param stagesIndex - 舞台索引序号列表
     * @param destIndex - 目标索引序号
     * @param sourceIndex - 源索引序号
     * @param activeIndex - 可选；激活索引序号
     */
    swapStages(stagesIndex: number[], destIndex: number, sourceIndex: number, activeIndex?: number) {
        const { oldIds, oldIndex, newIds, newIndex, oldStageSelection, newStageSelection } =
            this.service.frame.swapStages(stagesIndex, destIndex, sourceIndex, activeIndex);
        this.service.history.write({
            type: historyBaseType,
            data: {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
            },
            skipCheckOriginIndex: true,
        });
        return {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            oldStageSelection,
            newStageSelection,
        };
    }

    /**
     * 推入一个舞台数据，没有记录历史，初始化使用
     *
     * @param stages - 舞台数据
     * @param extraInfos - 舞台额外信息
     * @param activeNewStage - 是否激活新舞台
     * @param waitFontInit - 是否等到第一个舞台字体初始化完成
     */
    async insertWithoutHistory(
        stages: ILayerProps[][],
        extraInfos: IExtraInfo[],
        activeNewStage = false,
        waitFontInit = false,
    ) {
        // 没有运行期 unmatched 反馈后，初始 TextLayer 必须在首次排版前完成所需 fallback 注册。
        await this.service.font.loadFallbacks(stages.flat());

        // console.log('====insertWithoutHistory', stages, extraInfos, activeNewStage);
        this.service.frame.addExtraInfos(extraInfos);
        const { addList } = this.service.frame.addStages(stages, {
            activeNewStage,
            scaleWidth: false,
            originIndexes: [],
        });
    }

    /**
     * 聚焦指定容器
     * @param frameId - frameId
     * @param toCenter - 可选；是否居中；默认值为 `false`
     * @param focusPPTContainer - 可选；PPT 模式下是否聚焦 frame 所属 PPT 容器
     * @returns frameId
     */
    private focusBox(frameId: number, toCenter: boolean, focusPPTContainer = false) {
        if (toCenter) {
            setTimeout(() => {
                if (this.service.edgelessPPT.enable) {
                    if (focusPPTContainer) {
                        this.service.edgelessPPT.setFrameContainerToCenter(frameId);
                        return;
                    }
                    this.service.edgelessPPT.setContainerToCenter();
                } else {
                    this.service.edgelessCanvas.setFrameToCenter(frameId);
                }
            }, 100);
        }
        return frameId;
    }

    /**
     * 指定位置插入 frame
     *
     * @param position - 画布位置
     * @param layers - 图层数据
     * @param extraInfo - 可选；舞台额外信息；默认值为 `{}`
     * @param toCenter - 可选；是否居中；默认值为 `false`
     * @returns 插入的 frameId
     */
    async insertFrame(position: IPosition, layers: ILayerProps[], extraInfo: IExtraInfo = {}, toCenter = false) {
        if (!this.canInsertStages([extraInfo])) {
            this.warnMaxStages();
            return;
        }

        const { left, top } = position;
        const updateLayer = (layer: ILayerProps) => {
            layer.left += left;
            layer.top += top;
            layer.children?.forEach((child: ILayerProps) => {
                updateLayer(child);
            });
        };
        layers.forEach((layer: ILayerProps) => {
            updateLayer(layer);
        });
        const res = await this.batchInsert([layers], [extraInfo]);
        // console.log('res ===>', res[0], this.service.frame.getExtraInfoList());
        // console.log('stages ===>', this, this.service.frame.getFrames());
        return this.focusBox(res[0], toCenter);
    }

    /**
     * 创建空 frame
     *
     * @param args.x - frame 位置 x
     * @param args.y - frame 位置 y
     * @param args.width - frame 宽度
     * @param args.height - frame 高度
     * @param args.bgColor - 背景颜色
     * @param extraInfo - 可选；舞台额外信息；默认值为 `{}`
     * @param toCenter - 可选；是否居中；默认值为 `false`
     * @returns 插入的 frameId
     */
    async createFrame(args: IPosition & ISize & { bgColor?: string }, extraInfo: IExtraInfo = {}, toCenter = false) {
        const bgLayer = this.createEmptyBgLayer(args, args.bgColor || '#ffffff');
        return this.insertFrame(args, [bgLayer], extraInfo, toCenter);
    }

    /**
     * 创建PPT
     *
     * @param layers - 可选；初始图层列表
     * @param bgColor - 可选；背景颜色，如果 layers 里有背景图层，则不创建背景图层
     * @param originIndex - 可选；插入位置索引，正常插入不需要传入
     * @param taskId - 可选；PPT 容器归属的自动排版 taskId
     * @returns 创建的 frameId
     */
    async createPPT(args?: { layers?: ILayerProps[]; bgColor?: string; originIndex?: number; taskId?: string }) {
        const { layers, bgColor, originIndex, taskId } = args || {};
        const { edgelessPPT, frame } = this.service;
        const hasBgLayer = layers?.some((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);
        const stageLayers = hasBgLayer
            ? layers!
            : [
                  this.createEmptyBgLayer(
                      {
                          width: edgelessPPT.pptWidth,
                          height: edgelessPPT.pptHeight,
                      },
                      bgColor,
                  ),
                  ...(layers || []),
              ];
        const extraInfo: IExtraInfo = {
            isPPT: true,
        };
        if (taskId) {
            /** ppt 任务 id，用于后续 ppt 任务分组 */
            extraInfo.extraInfo = {
                taskId,
            };
        }
        if (!this.canInsertStages([extraInfo])) {
            this.warnMaxStages();
            return;
        }

        let res: number[] = [];
        if (originIndex) {
            res = await this.insertToIndex(stageLayers, extraInfo, false, frame.frameIdToIndex(originIndex));
        } else {
            res = await this.batchInsert([stageLayers], [extraInfo]);
        }
        return this.focusBox(res[0], true, true);
    }

    /**
     * 克隆 PPT
     * @param originIndex - 原始索引
     * @returns 克隆的 frameId
     */
    async clonePPT(originIndex: number) {
        const { frame } = this.service;
        const res = await this.clone(frame.frameIdToIndex(originIndex));
        if (!res.length) return;

        return this.focusBox(res[0], true, true);
    }

    /**
     * 上移 PPT
     * @param originIndex - 原始索引
     * @returns 上移的 frameId
     */
    async upPPT(originIndex: number) {
        const { frame } = this.service;
        const stageIndex = frame.frameIdToIndex(originIndex);
        this.swapStages([stageIndex], stageIndex - 1, stageIndex);
        return this.focusBox(originIndex, true);
    }

    /**
     * 下移 PPT
     * @param originIndex - 原始索引
     * @returns 下移的 frameId
     */
    async downPPT(originIndex: number) {
        const { frame } = this.service;
        const stageIndex = frame.frameIdToIndex(originIndex);
        this.swapStages([stageIndex], stageIndex + 1, stageIndex);
        return this.focusBox(originIndex, true);
    }

    /**
     * 删除 frame
     * @param originIndex - 原始索引
     * @param toCenter - 可选；是否居中；默认值为 `false`
     */
    async deleteFrame(originIndex: number) {
        const { frame: frameOperate } = this.service;
        frameOperate.delHoverId();
        this.delete(frameOperate.frameIdToIndex(originIndex));
    }

    /**
     * 批量插入并写入历史记录
     *
     * @param stages - 舞台列表
     * @param extraInfos - 额外信息
     * @param activeNewStage - 可选；是否激活的新舞台；默认值为 `false`
     * @param scaleWidth - 可选；缩放宽度；默认值为 `false`
     * @param trackFunc - 可选；跟踪函数
     * @param insertIndex - 可选；插入索引序号
     */
    async batchInsert(
        stages: ILayerProps[][],
        extraInfos: IExtraInfo[],
        activeNewStage = false,
        scaleWidth = false,
        trackFunc?: string,
        insertIndex?: number, // 新增插入位置参数
    ) {
        if (!this.canInsertStages(extraInfos)) {
            this.warnMaxStages();
            return [];
        }

        this.service.frame.delActiveLayer();
        let oldIds: number[] = [];
        let oldIndex: number = 0;
        let newIds: number[] = [];
        let newIndex: number = 0;
        const { originIndexs, insertId } = await this.service.history.realEdit.insertStage(extraInfos.length);
        const insertData = insertId
            ? cloneDeep({
                  insertId,
                  originIndexs,
                  extraInfos: extraInfos,
                  stages: scaleWidth ? [] : stages,
              })
            : undefined;
        this.service.frame.addExtraInfos(extraInfos, originIndexs);
        const finalAddList: number[] = [];
        for (let i = 0; i < stages.length; i++) {
            const stage = stages[i];
            const originIndex = originIndexs[i];
            // 如果指定了插入位置，则依次在该位置后插入 否则使用默认位置
            const currentInsertIndex = insertIndex !== undefined ? insertIndex + i : undefined;
            const {
                addList,
                oldIndex: oldActiveStageIndex,
                oldIds: oldStageList,
                newIndex: newActiveStageIndex,
                newIds: newStageList,
            } = this.service.frame.addStages(stage, {
                activeNewStage: i === 0 ? activeNewStage : false, // 需要激活第一个舞台
                stageIndex: currentInsertIndex,
                scaleWidth: scaleWidth,
                originIndexes: [originIndex],
            });
            if (scaleWidth && insertData) {
                insertData.stages.push(this.service.frame.getLayers(originIndex));
            }
            finalAddList.push(...addList);
            if (i === 0) {
                oldIds = oldStageList;
                oldIndex = oldActiveStageIndex;
            }
            newIds = newStageList;
            newIndex = newActiveStageIndex;
        }
        const data: IStageHistoryBaseData = {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
            insertData,
        };
        // console.log('data ===>', data);
        this.service.history.write({
            type: historyBaseType,
            data,
            trackFunc: trackFunc || trackConfig.page.add,
            skipCheckOriginIndex: true,
        });

        return difference(newIds, oldIds);
    }

    /**
     * 插入到下一个
     *
     * @param layers - 舞台数据
     * @param extraInfo - 舞台额外信息
     * @param scaleWidth - 可选；是否缩放宽度；默认值为 `false`
     * @param trackFunc - 可选；跟踪函数
     */
    async insertToNext(layers: ILayerProps[], extraInfo: IExtraInfo, scaleWidth = false, trackFunc?: string) {
        return this.insertStage(layers, extraInfo, scaleWidth, this.service.frame.getActiveIndex(), trackFunc);
    }

    /**
     * 插入到末尾
     *
     * @param layers - 舞台数据
     * @param extraInfo - 舞台额外信息
     * @param scaleWidth - 可选；是否缩放宽度；默认值为 `false`
     * @param trackFunc - 可选；跟踪函数
     */
    async insertToLast(layers: ILayerProps[], extraInfo: IExtraInfo, scaleWidth = false, trackFunc?: string) {
        return this.insertStage(layers, extraInfo, scaleWidth, undefined, trackFunc);
    }

    /**
     * 插入到指定位置
     *
     * @param layers - 舞台数据
     * @param extraInfo - 舞台额外信息
     * @param scaleWidth - 可选；是否缩放宽度；默认值为 `false`
     * @param index - 可选；插入位置
     * @param trackFunc - 可选；跟踪函数
     */
    async insertToIndex(
        layers: ILayerProps[],
        extraInfo: IExtraInfo,
        scaleWidth = false,
        index?: number,
        trackFunc?: string,
    ) {
        return this.insertStage(layers, extraInfo, scaleWidth, index, trackFunc);
    }

    /**
     * 获取视口宽度
     *
     * @returns 视口宽度
     */
    getViewWidth() {
        return this.service.edgelessCanvas.getSize().width;
    }

    /**
     * 获取视口高度
     *
     * @returns 视口高度
     */
    getViewHeight() {
        return this.service.edgelessCanvas.getSize().height;
    }

    /**
     * 监听舞台重新渲染
     * @param originIndex - 舞台原始索引
     * @param fn - 回调函数
     * @param weight - 可选；权重；默认值为 `0`
     * @returns 监听器
     */
    onRerenderStage(originIndex: number, fn: (originIndex: number) => void, weight = 0) {
        return this.service.frame.onRerenderAll((_renderFrameIds, diff) => {
            if (diff.includes(originIndex)) {
                fn(originIndex);
            }
        }, weight);
    }

    /**
     * 监听舞台尺寸变化
     *
     * @param fn - 回调函数
     * @param weight - 可选；权重；默认值为 `0`
     * @returns 监听器
     */
    onResize(fn: (args: IStageResizeData[]) => void, weight: number = 0) {
        return this.service.frame.onResize((frameId) => {
            fn([
                {
                    originIndex: frameId,
                    stageIndex: this.service.frame.frameIdToIndex(frameId),
                    size: this.service.frame.getSize(frameId),
                },
            ]);
        }, weight);
    }

    /**
     * 缩放图层
     *
     * @param layer - 图层数据
     * @param offsetWidth - 偏移宽度
     * @param direction - 方向
     * @param disableWithGroupHasMinLayer - 可选；是否禁用组最小图层
     * @param callback - 可选；回调函数
     */
    scaleLayer(
        layer: ILayerProps,
        offsetWidth: number,
        direction = DirectionType.POSITIVE,
        disableWithGroupHasMinLayer = true,
        callback?: (layer: ILayerProps) => void,
    ) {
        this.service.frame.scaleLayer(layer, offsetWidth, direction, disableWithGroupHasMinLayer, false, callback);
    }

    /** 隐藏素材层 */
    hideMaterial(disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
        return this.service.frame.toggleMaterial(true, disableHideFn, originIndex);
    }

    /** 显示素材层 */
    showMaterial(disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
        return this.service.frame.toggleMaterial(false, disableHideFn, originIndex);
    }
}
